import React, {
  forwardRef,
  useEffect,
  useRef,
  type PropsWithChildren,
} from "react"
import type { ClassNameValue } from "tailwind-merge"

import { AnimationRouterContext } from "@be-miniapp/ui/components/animated-context"
import {
  ActionBar,
  type ActionBarProps,
} from "@be-miniapp/ui/components/app-action-bar"
import { cn } from "@be-miniapp/ui/lib/utils"
import { HARDWARE_ACCELERATION_STYLES_V12 } from "@be-miniapp/ui/utils/animation-config-v12"

import { useMobileAnimation } from "../hooks/use-mobile-animation"

interface LayoutProps extends PropsWithChildren {
  showActionBar?: boolean
  actionBarProps?: ActionBarProps
  className?: ClassNameValue
  name?: string
  animate?: boolean
}

const Layout = forwardRef(
  (
    {
      showActionBar,
      actionBarProps,
      children,
      className,
      animate = true,
    }: LayoutProps,
    ref
  ) => {
    const animateContext = React.useContext(AnimationRouterContext)
    // NativePageTransition owns route push/pop when the animation provider is mounted.
    const shouldAnimate = animate && animateContext == null

    const {
      initial,
      animate: animateTarget,
      transition,
    } = useMobileAnimation({
      shouldAnimate,
      direction: animateContext?.direction || "forward",
    })

    const divRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
      const el = divRef.current
      if (!el) return

      if (!shouldAnimate) {
        el.style.transition = "none"
        el.style.transform = "none"
        el.style.opacity = "1"
        return
      }

      let cancelled = false
      let transitionEndListener: ((event: TransitionEvent) => void) | null =
        null

      const resetToFinal = () => {
        if (cancelled) return
        el.style.transition = "none"
        el.style.transform = "translateX(0%)"
        el.style.opacity = "1"
      }

      el.style.transition = "none"
      Object.assign(el.style, initial)

      const frame = requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (cancelled) return

          el.style.transition = transition
          Object.assign(el.style, animateTarget)

          transitionEndListener = (event: TransitionEvent) => {
            if (event.target !== el || event.propertyName !== "transform")
              return
            if (transitionEndListener) {
              el.removeEventListener("transitionend", transitionEndListener)
            }
            el.style.transition = "none"
          }

          el.addEventListener("transitionend", transitionEndListener)
        })
      })

      const fallback = window.setTimeout(resetToFinal, 400)

      return () => {
        cancelled = true
        cancelAnimationFrame(frame)
        window.clearTimeout(fallback)
        if (transitionEndListener) {
          el.removeEventListener("transitionend", transitionEndListener)
        }
      }
    }, [shouldAnimate, initial, animateTarget, transition])

    return (
      <div
        ref={divRef}
        className={cn(
          "no-scrollbar mx-auto h-screen w-screen overflow-hidden bg-be-neutral-25",
          className
        )}
        style={
          animateContext != null ? undefined : HARDWARE_ACCELERATION_STYLES_V12
        }
      >
        <div className="flex size-full flex-col">
          {showActionBar && <ActionBar {...actionBarProps} />}

          <div className="relative flex-1">
            <div className="absolute inset-0">
              <div
                id="scrollable-area"
                ref={ref as React.Ref<HTMLDivElement>}
                className="no-scrollbar size-full overflow-x-hidden overflow-y-scroll scroll-smooth"
                style={{
                  WebkitOverflowScrolling: "touch",
                  overscrollBehavior: "contain",
                }}
              >
                {children}
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }
)

Layout.displayName = "Layout"

export { Layout }
